import {
  Box,
  Card,
  Flex,
  PrimaryButton,
  SecondaryButton,
  space,
  Table,
  TertiaryButton,
  Text
} from '@workday/canvas-kit-react'
import { arrowLeftIcon, linkIcon, trashIcon } from '@workday/canvas-system-icons-web'
import React, { useContext, useEffect, useState } from 'react'
import FeedConfiguration from '../models/FeedConfiguration'
import { loadSavedFeeds, updateSavedFeeds } from '../models/SavedFeeds'
import { fetchAgencies } from '../models/WebAppRequests'
import { PPLoadingDots } from '../../components/PPLoadingDots'
import { CenteredLayout } from '../../components/CenteredLayout'
import { PrimaryButtonColors } from '../../components/CanvasThemes'
import { EmptyState } from '../../components/EmptyState'
import { PPTableRow } from '../../components/PPTableRow'
import { UpdateToastContext } from '../../utils/UpdateToastContext'
import { environmentDescription } from '../../models/Environment'
import { AgencyModel } from '../../models/AgencyModel'
import { UpdateConfirmationContext } from '../../utils/UpdateConfirmationContext'

export type ManageSavedFeedsProps = {
  didSelectBack: () => void
  onViewSavedFeed: (feed: FeedConfiguration) => void
}

const agencyIDsFromFeeds = (feeds: FeedConfiguration[]) => {
  const agencyIDSet = new Set<string>()
  feeds.forEach(feed => {
    feed.agencyIDs.forEach(agencyID => agencyIDSet.add(agencyID))
  })

  const agencyIDs: string[] = []
  agencyIDSet.forEach(agencyID => agencyIDs.push(agencyID))
  return agencyIDs
}

const deleteFeed = (feed: FeedConfiguration) => {
  const savedFeeds = loadSavedFeeds().filter(f => f.id !== feed.id)
  updateSavedFeeds(savedFeeds)
}

const ManageSavedFeeds = ({ didSelectBack, onViewSavedFeed }: ManageSavedFeedsProps) => {
  const updateConfirmation = useContext(UpdateConfirmationContext)
  const toast = useContext(UpdateToastContext)
  const [loadingAgencies, setLoadingAgencies] = useState(false)
  const [agencyPool, setAgencyPool] = useState<AgencyModel[]>([])
  const savedFeeds = loadSavedFeeds().sort((a, b) => a.name.localeCompare(b.name))
  const hasSandbox = savedFeeds.find(feed => feed.environment === 'SANDBOX') != undefined

  const agencyIDs = agencyIDsFromFeeds(savedFeeds)
  const loadedAgencyIDs = agencyPool.map(agency => agency.agencyID)
  const agencyIDsToLoad = agencyIDs.filter(agencyID => !loadedAgencyIDs.includes(agencyID)) as string[]
  useEffect(() => {
    if (agencyIDsToLoad.length > 0) {
      setLoadingAgencies(true)
      fetchAgencies(agencyIDsToLoad, '', false, hasSandbox ? 'SANDBOX' : 'PRODUCTION', undefined)
        .then(loadedAgencies => {
          setAgencyPool(prev => prev.concat(loadedAgencies))
        })
        .finally(() => setLoadingAgencies(false))
    }
  })

  return loadingAgencies ? (
    <PPLoadingDots />
  ) : (
    <CenteredLayout>
      <Box>
        <SecondaryButton icon={arrowLeftIcon} onClick={() => didSelectBack()}>
          Back
        </SecondaryButton>
      </Box>
      {savedFeeds.length > 0 ? (
        <Card>
          <Card.Heading>Saved Feeds</Card.Heading>
          <Card.Body>
            <Table>
              <tbody>
                <PPTableRow header>
                  <td>Name</td>
                  <td>Agencies</td>
                  <td>Incident Types</td>
                  {hasSandbox && <td>Environment</td>}
                  <td></td>
                </PPTableRow>
                {savedFeeds.map(feed => (
                  <PPTableRow>
                    <td>
                      <Text fontWeight="medium">{feed.name}</Text>
                    </td>
                    <td>
                      <Text whiteSpace="pre-line">
                        {feed.agencyIDs
                          .map(agencyID => agencyPool.find(agency => agency.agencyID === agencyID)?.shortName ?? '')
                          .filter(value => value !== '')
                          .join('\n')}
                      </Text>
                    </td>
                    <td>
                      <Text whiteSpace="pre-line">
                        {feed.callTypes.length === 0
                          ? 'All Incident Types'
                          : feed.callTypes.map(callType => callType.description).join('\n')}
                      </Text>
                      {feed.hideMedical && 
                        <div>
                          <Text whiteSpace="pre-line">
                            No Medical Incidents
                          </Text>
                        </div>
                      }
                      {feed.hideRecent && 
                        <div>
                          <Text whiteSpace="pre-line">
                            No Recent Incidents
                          </Text>
                        </div>
                      }
                    </td>
                    {hasSandbox && <td>{environmentDescription(feed.environment)}</td>}
                    <td width="100px">
                      <Flex flexDirection="row" alignItems="center" gap={space.xxs}>
                        <PrimaryButton
                          colors={PrimaryButtonColors}
                          size="extraSmall"
                          onClick={event => onViewSavedFeed(feed)}
                        >
                          View
                        </PrimaryButton>
                        <TertiaryButton
                          icon={linkIcon}
                          onClick={event => {
                            navigator.clipboard.writeText(feed.link())
                            toast.setMessages(['Copied to Clipboard'], [])
                          }}
                        />
                        <TertiaryButton
                          icon={trashIcon}
                          onClick={event =>
                            updateConfirmation.setConfirmation({
                              title: '',
                              message: `Are you sure you want to delete '${feed.name}'?`,
                              onCancel: () => {},
                              onConfirm: () => {
                                deleteFeed(feed)
                              }
                            })
                          }
                        />
                      </Flex>
                    </td>
                  </PPTableRow>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <EmptyState>
          <EmptyState.Heading>No Saved Feeds Found</EmptyState.Heading>
        </EmptyState>
      )}
    </CenteredLayout>
  )
}

export default ManageSavedFeeds
